import axiosInstance from './axiosInterceptor';

/**
 * Si no se usa el interceptor para gestionar las
 * credenciales se ha de des comentar la creación
 * de la instancia de Axios.
 *
 * import axios from 'axios';
 * const axiosInstance = axios.create({
 *   baseURL: '/rest/'
 * });
 */

/**
 * @template T
 * @typedef {import ('../interfaces/utils/axios.interface').ProcessedData<T>} ProcessedData
 */

/**
 * @typedef {import ('../interfaces/utils/axios.interface').ExternalApiParams} ExternalApiParams
 */

/**
 * Procesa un error en la llamada, en caso de ser un error
 * no controlado en el back genera un error 400 - Axios - Bad request
 * @template T
 * @param {Object} response
 * @param {Function} [errBack]
 * @return {ProcessedData<T>}
 */
const _processError = (response, errBack) => {
  const status = response?.response?.status || 400;
  const error = response?.response?.data?.message || { message: 'Axios - Bad request' };
  const message = { status, data: null, error };

  if (errBack) return errBack(message);
  return message;
};

/**
 * Procesa un llamada correcta
 * @template T
 * @param {Object} response
 * @param {Function} [callBack]
 * @return {ProcessedData<T>}
 */
const _processResponse = (response, callBack) => {
  if (callBack) return callBack(response.data);
  if (response.status >= 200 && response.status < 300) return { status: response.status, data: response.data };
  return _processError(response);
};

/**
 * Realiza una llamada a la url informada y devuelve el resultado.
 * En caso de error se puede reintentar.
 * @async
 * @template T
 * @param {ExternalApiParams} params
 * @return {Promise<ProcessedData<T>>}
 */
const callGetExternalApi = async ({ url, headers, responseType, params, data, numTries, callBack, errBack }) => {
  try {
    const callResponse = await axiosInstance.get(url, {
      data,
      params,
      headers,
      responseType,
    });

    return _processResponse(callResponse, callBack);
  } catch (err) {
    return numTries && numTries > 0
      ? callGetExternalApi({ url, headers, responseType, params, data, numTries: numTries - 1, callBack, errBack })
      : _processError(err, errBack);
  }
};

/**
 * Realiza una llamada a la url informada y devuelve el resultado.
 * En caso de error se puede reintentar.
 * @async
 * @template T
 * @param {ExternalApiParams} params
 * @return {Promise<ProcessedData<T>>}
 */
const callPostExternalApi = async ({ url, headers, params, data, numTries, callBack, errBack }) => {
  try {
    const callResponse = await axiosInstance.post(url, data, {
      params,
      headers,
    });

    return _processResponse(callResponse, callBack);
  } catch (err) {
    return numTries && numTries > 0
      ? callPostExternalApi({ url, headers, params, data, numTries: numTries - 1, callBack, errBack })
      : _processError(err, errBack);
  }
};

/**
 * Realiza una llamada a la url informada y devuelve el resultado.
 * En caso de error se puede reintentar.
 * @async
 * @template T
 * @param {ExternalApiParams} params
 * @return {Promise<ProcessedData<T>>}
 */
const callPutExternalApi = async ({ url, headers, params, data, numTries, callBack, errBack }) => {
  try {
    const callResponse = await axiosInstance.put(url, data, {
      params,
      headers,
    });
    return _processResponse(callResponse, callBack);
  } catch (err) {
    return numTries && numTries > 0
      ? callPutExternalApi({ url, headers, params, data, numTries: numTries - 1, callBack, errBack })
      : _processError(err, errBack);
  }
};

/**
 * Realiza una llamada a la url informada y devuelve el resultado.
 * En caso de error se puede reintentar.
 * @async
 * @template T
 * @param {ExternalApiParams} params
 * @return {Promise<ProcessedData<T>>}
 */
const callDeleteExternalApi = async ({ url, headers, params, data, numTries, callBack, errBack }) => {
  try {
    const callResponse = await axiosInstance.delete(url, {
      data,
      params,
      headers,
    });
    return _processResponse(callResponse, callBack);
  } catch (err) {
    return numTries && numTries > 0
      ? callDeleteExternalApi({ url, headers, params, data, numTries: numTries - 1, callBack, errBack })
      : _processError(err, errBack);
  }
};

export { callGetExternalApi, callPostExternalApi, callPutExternalApi, callDeleteExternalApi };
